import { RefObject, useEffect, useRef, useState } from 'react';

export const HOLD_MS = 10_000;
type Rendition = 'mobile' | 'desktop';
type Connection = EventTarget & { saveData?: boolean };
type Controller = {
  attach: (video: HTMLVideoElement) => void;
  detach: (video: HTMLVideoElement) => void;
};

export function useBannerAnimation(
  videoRef: RefObject<HTMLVideoElement | null>,
  containerRef: RefObject<HTMLDivElement | null>,
) {
  const [rendition, setRendition] = useState<Rendition | null>(null);
  const [started, setStarted] = useState(false);
  const controllerRef = useRef<Controller | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const breakpoint = window.matchMedia?.('(max-width: 900px)');
    const connection = (navigator as Navigator & { connection?: Connection }).connection;
    let video: HTMLVideoElement | null = null;
    let removeMediaListeners = () => {};
    let requested: Rendition | null = null;
    let visible = !('IntersectionObserver' in window);
    let disposed = false;
    let ready = false;
    let blocked = false;
    let failed = false;
    let holding = false;
    let position = 0;
    let remaining = HOLD_MS;
    let deadline = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let generation = 0;
    let pendingPlay = false;
    const staticOnly = () => !!motion?.matches || !!connection?.saveData;
    const canRun = () => !disposed && !staticOnly() && !failed && !blocked && visible && !document.hidden;
    const stopTimer = () => {
      if (timer !== undefined) {
        remaining = Math.max(0, deadline - performance.now());
        clearTimeout(timer);
        timer = undefined;
      }
    };
    const play = () => {
      if (!video || pendingPlay) return;
      const current = video;
      const attempt = generation;
      pendingPlay = true;
      Promise.resolve(current.play())
        .catch(() => {
          if (disposed || attempt !== generation || current !== video || !canRun()) return;
          blocked = true;
          setStarted(false);
        })
        .finally(() => {
          if (attempt === generation) pendingPlay = false;
        });
    };
    const sync = () => {
      if (!canRun() || !ready || !video) {
        stopTimer();
        video?.pause();
        return;
      }
      if (holding) {
        if (timer === undefined) {
          deadline = performance.now() + remaining;
          timer = setTimeout(() => {
            timer = undefined;
            holding = false;
            position = 0;
            remaining = HOLD_MS;
            if (video) {
              video.currentTime = 0;
              play();
            }
          }, remaining);
        }
      } else if (video.paused) play();
    };
    const detach = (current: HTMLVideoElement) => {
      if (video !== current) return;
      stopTimer();
      if (!holding && ready) position = current.currentTime;
      ++generation;
      pendingPlay = false;
      ready = false;
      removeMediaListeners();
      current.pause();
      video = null;
    };
    const selectRendition = () => {
      const next = staticOnly() ? null : breakpoint?.matches ? 'mobile' : 'desktop';
      if (next === requested) return;
      if (video) detach(video);
      requested = next;
      setStarted(false);
      if (next === null) {
        holding = false;
        position = 0;
        remaining = HOLD_MS;
      }
      setRendition(next);
    };
    const attach = (current: HTMLVideoElement) => {
      if (disposed || staticOnly()) return;
      video = current;
      const attachment = ++generation;
      const active = () => !disposed && video === current && attachment === generation;
      let restoring = true;
      let restoreRequested = false;
      const restored = () => {
        if (!active()) return;
        restoring = false;
        ready = true;
        // A sought frame can be displayed while paused or holding.
        if (position > 0 || holding) setStarted(true);
        sync();
      };
      const metadata = () => {
        if (!active() || restoreRequested) return;
        restoreRequested = true;
        const lastFrame = Math.max(0, current.duration - 1 / 24);
        const target = holding ? lastFrame : Math.min(position, lastFrame);
        if (target > 0 && Math.abs(current.currentTime - target) > 0.01) {
          current.currentTime = target;
        } else restored();
      };
      const seeked = () => {
        if (restoring && restoreRequested) restored();
      };
      const canplay = () => {
        if (!active()) return;
        if (restoring) {
          if (!restoreRequested) metadata();
        } else sync();
      };
      const playing = () => {
        if (!active()) return;
        if (!canRun() || !ready) {
          current.pause();
          return;
        }
        pendingPlay = false;
        setStarted(true);
      };
      const ended = () => {
        if (!active() || !ready || staticOnly() || failed) return;
        pendingPlay = false;
        holding = true;
        remaining = HOLD_MS;
        sync();
      };
      const error = () => {
        if (!active() || staticOnly()) return;
        failed = true;
        stopTimer();
        current.pause();
        setStarted(false);
      };
      const listeners: [string, () => void][] = [
        ['loadedmetadata', metadata],
        ['seeked', seeked],
        ['canplay', canplay],
        ['playing', playing],
        ['ended', ended],
        ['error', error],
      ];
      listeners.forEach(([event, handler]) => current.addEventListener(event, handler));
      removeMediaListeners = () =>
        listeners.forEach(([event, handler]) => current.removeEventListener(event, handler));
      current.load();
    };
    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => {
            visible = entries.some((entry) => entry.isIntersecting);
            sync();
          })
        : null;
    observer?.observe(container);
    document.addEventListener('visibilitychange', sync);
    motion?.addEventListener?.('change', selectRendition);
    breakpoint?.addEventListener?.('change', selectRendition);
    connection?.addEventListener('change', selectRendition);
    controllerRef.current = { attach, detach };
    selectRendition();
    return () => {
      disposed = true;
      if (video) detach(video);
      stopTimer();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', sync);
      motion?.removeEventListener?.('change', selectRendition);
      breakpoint?.removeEventListener?.('change', selectRendition);
      connection?.removeEventListener('change', selectRendition);
      controllerRef.current = null;
    };
  }, [containerRef]);

  useEffect(() => {
    const video = videoRef.current;
    const controller = controllerRef.current;
    if (!video || !rendition || !controller) return;
    controller.attach(video);
    return () => controller.detach(video);
  }, [rendition, videoRef]);

  return { rendition, started };
}
